import { Link as RouterLink, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { changelogData } from "@/config/changelog-data";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DUAL_AXIS_NAVIGATION, getActiveDomainAndTab, DomainItem, SubTabItem } from "@/config/navigation";
import {
    LayoutDashboard, ShoppingCart, FileText, Settings, Factory, Package, ClipboardCheck, Truck, Box,
    ChevronDown, ChevronRight, Users, ShieldCheck, Database, Users2, Layers, Scissors, Activity,
    Warehouse, ClipboardList, Cpu, Briefcase, HelpCircle, BookOpen, History, Menu, type LucideIcon,
    CheckCircle2, AlertTriangle, FileWarning, Microscope, Container,
    KanbanSquare, MonitorPlay, ArrowRightLeft, CreditCard, PieChart, BarChart, GitBranch, Printer,
    MessageSquarePlus, ScanBarcode, Trash2, Percent, PenTool, Copy, Wrench, FileSpreadsheet, PackageCheck, Calculator,
    Tv, Gauge
} from "lucide-react";
import { useSidebar } from "@/layouts/RootLayout";
import { useConnectionStatus } from "@/hooks/useConnectionStatus";
import { useAuthStore } from "@/stores/auth.store";
import { useUserPreferences } from "@/stores/useUserPreferences";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const IconMap: Record<string, LucideIcon> = {
    LayoutDashboard, ShoppingCart, FileText, Settings, Factory, Package, ClipboardCheck, Truck, Box,
    Users, ShieldCheck, Database, Users2, Layers, Scissors, Activity, Warehouse, ClipboardList, Cpu, Briefcase,
    HelpCircle, BookOpen, History, Menu, CheckCircle2, AlertTriangle, FileWarning, Microscope, Container,
    KanbanSquare, MonitorPlay, ArrowRightLeft, CreditCard, PieChart, BarChart, GitBranch, Printer,
    MessageSquarePlus, ScanBarcode, Trash2, Percent, PenTool, Copy, Wrench, FileSpreadsheet, PackageCheck, Calculator,
    Tv, Gauge
};

/** Nhóm menu mở sẵn khi vào hệ thống (id trong config/navigation) */
const DEFAULT_OPEN_DOMAINS: Record<string, boolean> = { sales: true, production_planning: true, shopfloor: true };

export function Sidebar({ isCollapsed }: { isCollapsed: boolean }) {
    const router = useRouter();
    const pathname = router.state.location.pathname;
    const searchObj = router.state.location.search as any;

    let searchString = "";
    if (searchObj && typeof searchObj === "object" && Object.keys(searchObj).length > 0) {
        const params = new URLSearchParams();
        Object.entries(searchObj).forEach(([k, v]) => {
            if (v !== undefined && v !== null) params.append(k, String(v));
        });
        searchString = `?${params.toString()}`;
    }

    const { toggleSidebar } = useSidebar();
    const user = useAuthStore((s) => s.user);
    const { hasPermission } = useRoleAccess();
    
    // Detect mobile for forcing treeview
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userPrefLayout = useUserPreferences((s) => s.menuLayout || 'treeview');
    const menuLayout = isMobile ? 'treeview' : userPrefLayout;

    const { activeDomain, activeTab } = getActiveDomainAndTab(pathname, searchString);

    const filteredDomains = DUAL_AXIS_NAVIGATION.map((domain) => {
        if (domain.roles && (!user || !domain.roles.includes(user.role))) {
            return null;
        }
        const visibleTabs = domain.tabs.filter((tab) => {
            if (tab.roles && (!user || !tab.roles.includes(user.role))) return false;
            if (tab.moduleId && !hasPermission(tab.moduleId, "view")) return false;
            return true;
        });
        if (visibleTabs.length === 0) return null;
        return {
            ...domain,
            defaultHref: visibleTabs[0].href,
            tabs: visibleTabs,
        };
    }).filter((d): d is DomainItem => d !== null);

    // Accordion state for TreeView mode
    // Mặc định mở sẵn 3 nhóm dùng nhiều nhất: Kinh doanh, Kế hoạch, Xưởng sản xuất (người dùng vẫn thu gọn được)
    const [openDomains, setOpenDomains] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = { ...DEFAULT_OPEN_DOMAINS };
        if (activeDomain) {
            initial[activeDomain.id] = true;
        }
        return initial;
    });

    // Auto expand active domain when route changes
    useEffect(() => {
        if (activeDomain) {
            setOpenDomains(prev => ({
                ...prev,
                [activeDomain.id]: true
            }));
        }
    }, [activeDomain?.id]);

    const toggleDomainOpen = (domainId: string) => {
        setOpenDomains(prev => ({
            ...prev,
            [domainId]: !prev[domainId]
        }));
    };

    return (
        <div data-testid="app-sidebar" className="flex flex-col h-full shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-20 select-none" style={{ background: 'linear-gradient(180deg, var(--apv-sidebar-bg) 0%, color-mix(in srgb, var(--apv-sidebar-bg) 95%, black) 100%)', color: 'var(--apv-sidebar-text)' }}>
            {/* Logo Area */}
            <div
                className={cn("h-14 flex items-center transition-all relative z-10 cursor-pointer hover:bg-white/5", isCollapsed ? "justify-center px-0" : "px-3")}
                style={{ borderBottom: '1px solid color-mix(in srgb, var(--apv-sidebar-border) 40%, transparent)' }}
                onClick={toggleSidebar}
            >
                <div className={cn("flex items-center gap-2 overflow-hidden", isCollapsed ? "justify-center w-full" : "ml-1")}>
                    <div className="shrink-0 flex items-center justify-center">
                        <img
                            src="/artpresto-logo.png"
                            alt="ARTPRESTO"
                            className="h-7 w-7 object-contain rounded bg-white p-0.5 shadow-sm"
                        />
                    </div>
                    {!isCollapsed && (
                        <div className="flex flex-col">
                            <span className="font-bold tracking-tight text-sm whitespace-nowrap text-white">ARTPRESTO ERP</span>
                            <span className="text-[9px] text-slate-400 font-medium">
                                {menuLayout === 'treeview' ? 'Tree View Menu' : 'Dual Axis Menu'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Body */}
            <div className="flex-1 overflow-y-auto py-3 custom-scrollbar">
                {menuLayout === 'treeview' ? (
                    /* ─── TREEVIEW MODE ─── */
                    <div className="space-y-2 px-2">
                        {filteredDomains.map((domain, index) => {
                            const prevDomain = index > 0 ? filteredDomains[index - 1] : null;
                            const isNewSection = !prevDomain || prevDomain.section !== domain.section;
                            const DomainIcon = (domain.icon && IconMap[domain.icon]) || Box;
                            const isDomainActive = activeDomain?.id === domain.id;
                            const isOpen = isCollapsed ? false : (openDomains[domain.id] ?? isDomainActive);

                            return (
                                <div key={domain.id} className="space-y-1">
                                    {isNewSection && (
                                        !isCollapsed ? (
                                            <div className={cn("pt-3 pb-1 px-2.5 flex items-center gap-2 select-none", index === 0 && "pt-0.5")}>
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400/90 whitespace-nowrap">
                                                    {domain.section}
                                                </span>
                                                <div className="h-px bg-white/10 flex-1" />
                                            </div>
                                        ) : (
                                            index > 0 && <div className="my-2 mx-2 border-t border-white/10" />
                                        )
                                    )}

                                    {/* Domain Header: Direct Link if 1 tab, Accordion Toggle if multiple tabs */}
                                    {domain.tabs.length === 1 ? (
                                        <RouterLink
                                            to={domain.defaultHref as any}
                                            className={cn(
                                                "w-full flex items-center text-left py-2 px-2.5 rounded-lg text-xs font-bold tracking-wide transition-all group",
                                                isDomainActive
                                                    ? "bg-blue-600 text-white shadow-xs shadow-blue-500/20"
                                                    : "text-slate-300 hover:bg-white/5 hover:text-white",
                                                isCollapsed && "justify-center px-0 py-2.5"
                                            )}
                                            data-active={isDomainActive ? "true" : "false"}
                                            title={isCollapsed ? domain.title : undefined}
                                        >
                                            <DomainIcon className={cn(
                                                "h-4 w-4 shrink-0 transition-transform group-hover:scale-110",
                                                isDomainActive ? "text-white" : "text-slate-400 group-hover:text-slate-200",
                                                !isCollapsed && "mr-2.5"
                                            )} />
                                            {!isCollapsed && (
                                                <span className="flex-1 truncate uppercase text-[11px] font-bold tracking-wider">
                                                    {domain.title}
                                                </span>
                                            )}
                                        </RouterLink>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isCollapsed) {
                                                        router.navigate({ to: domain.defaultHref as any });
                                                    } else {
                                                        toggleDomainOpen(domain.id);
                                                    }
                                                }}
                                                className={cn(
                                                    "w-full flex items-center text-left py-2 px-2.5 rounded-lg text-xs font-bold tracking-wide transition-all group",
                                                    isDomainActive
                                                        ? "bg-white/10 text-white shadow-xs"
                                                        : "text-slate-300 hover:bg-white/5 hover:text-white",
                                                    isCollapsed && "justify-center px-0 py-2.5"
                                                )}
                                                data-active={isDomainActive ? "true" : "false"}
                                                title={isCollapsed ? domain.title : undefined}
                                            >
                                                <DomainIcon className={cn(
                                                    "h-4 w-4 shrink-0 transition-transform group-hover:scale-110",
                                                    isDomainActive ? "text-blue-400" : "text-slate-400 group-hover:text-slate-200",
                                                    !isCollapsed && "mr-2.5"
                                                )} />
                                                
                                                {!isCollapsed && (
                                                    <>
                                                        <span className="flex-1 truncate uppercase text-[11px] font-bold tracking-wider">
                                                            {domain.title}
                                                        </span>
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-slate-400 font-mono mr-1">
                                                            {domain.tabs.length}
                                                        </span>
                                                        {isOpen ? (
                                                            <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform duration-200" />
                                                        ) : (
                                                            <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform duration-200" />
                                                        )}
                                                    </>
                                                )}
                                            </button>

                                            {/* Sub-tab Child List */}
                                            {!isCollapsed && isOpen && (
                                                <div className="ml-3 pl-2.5 border-l border-white/10 space-y-0.5 py-1">
                                                    {domain.tabs.map((tab) => {
                                                        const TabIcon = tab.icon ? IconMap[tab.icon] || FileText : FileText;

                                                        const isTabActive = activeTab?.href === tab.href || (() => {
                                                            if (tab.href.includes('?')) {
                                                                const [tabPath, tabSearch] = tab.href.split('?');
                                                                return pathname.replace(/\/$/, '') === tabPath.replace(/\/$/, '') && searchString.includes(tabSearch);
                                                            }
                                                            const cleanPath = pathname.replace(/\/$/, '') || '/';
                                                            const tabCleanHref = tab.href.replace(/\/$/, '');
                                                            if (cleanPath === tabCleanHref && !searchString.includes('domain=procurement')) return true;
                                                            if (tabCleanHref !== '/' && cleanPath.startsWith(tabCleanHref + '/')) return true;
                                                            return false;
                                                        })();

                                                        return (
                                                            <RouterLink
                                                                key={tab.title + tab.href}
                                                                to={tab.href as any}
                                                                data-active={isTabActive ? "true" : "false"}
                                                                className={cn(
                                                                    "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-all group select-none",
                                                                    isTabActive
                                                                        ? "bg-blue-600 text-white font-semibold shadow-xs shadow-blue-500/20"
                                                                        : "text-slate-300/80 hover:text-white hover:bg-white/10"
                                                                )}
                                                            >
                                                                <TabIcon className={cn(
                                                                    "h-3.5 w-3.5 shrink-0 transition-transform group-hover:scale-105",
                                                                    isTabActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"
                                                                )} />
                                                                <span className="truncate flex-1">{tab.title}</span>
                                                                {tab.badge && (
                                                                    <span className={cn(
                                                                        "px-1.5 py-0.2 text-[9px] font-bold rounded-full",
                                                                        isTabActive ? "bg-white/20 text-white" : "bg-blue-900/60 text-blue-300"
                                                                    )}>
                                                                        {tab.badge}
                                                                    </span>
                                                                )}
                                                            </RouterLink>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* ─── DUAL-AXIS MODE (DEFAULT) ─── */
                    <div className="space-y-1.5 px-2">
                        {filteredDomains.map((domain, index) => {
                            const prevDomain = index > 0 ? filteredDomains[index - 1] : null;
                            const isNewSection = !prevDomain || prevDomain.section !== domain.section;
                            const DomainIcon = (domain.icon && IconMap[domain.icon]) || Box;
                            const isDomainActive = activeDomain?.id === domain.id;

                            return (
                                <div key={domain.id} className="space-y-1.5">
                                    {isNewSection && (
                                        !isCollapsed ? (
                                            <div className={cn("pt-3 pb-1 px-2.5 flex items-center gap-2 select-none", index === 0 && "pt-0.5")}>
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400/90 whitespace-nowrap">
                                                    {domain.section}
                                                </span>
                                                <div className="h-px bg-white/10 flex-1" />
                                            </div>
                                        ) : (
                                            index > 0 && <div className="my-2 mx-2 border-t border-white/10" />
                                        )
                                    )}

                                    <Button
                                        variant="ghost"
                                        className={cn(
                                            "w-full justify-start h-11 transition-all duration-200 group relative rounded-lg font-medium",
                                            isDomainActive
                                                ? "bg-blue-600/90 text-white shadow-md shadow-blue-600/20"
                                                : "text-slate-300 hover:bg-white/10 hover:text-white",
                                            isCollapsed && "justify-center px-0"
                                        )}
                                        asChild
                                        title={isCollapsed ? domain.title : undefined}
                                    >
                                        <RouterLink to={domain.defaultHref}>
                                            <DomainIcon className={cn(
                                                "h-5 w-5 shrink-0 transition-transform group-hover:scale-110",
                                                isDomainActive ? "text-white" : "opacity-80 group-hover:opacity-100",
                                                !isCollapsed && "mr-3"
                                            )} />
                                            {!isCollapsed && (
                                                <div className="flex flex-col text-left overflow-hidden">
                                                    <span className="text-xs font-bold tracking-wide truncate">{domain.title}</span>
                                                    <span className="text-[10px] opacity-60 font-normal truncate">
                                                        {domain.tabs.length} mục quy trình
                                                    </span>
                                                </div>
                                            )}
                                            {isDomainActive && !isCollapsed && (
                                                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                            )}
                                        </RouterLink>
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer */}
            <SidebarFooter isCollapsed={isCollapsed} />
        </div>
    );
}

function SidebarFooter({ isCollapsed }: { isCollapsed: boolean }) {
    const { status, label, latency } = useConnectionStatus();
    const statusConfig = {
        online: {
            dot: 'bg-emerald-400',
            glow: 'shadow-[0_0_6px_rgba(52,211,153,0.5)]',
            text: 'text-emerald-400',
            ringColor: 'ring-emerald-400/30',
        },
        mockup: {
            dot: 'bg-amber-400',
            glow: 'shadow-[0_0_6px_rgba(251,191,36,0.5)]',
            text: 'text-amber-400',
            ringColor: 'ring-amber-400/30',
        },
        disconnected: {
            dot: 'bg-red-400',
            glow: 'shadow-[0_0_6px_rgba(248,113,113,0.5)]',
            text: 'text-red-400',
            ringColor: 'ring-red-400/30',
        },
    };

    const cfg = statusConfig[status];

    return (
        <div className="p-3" style={{ borderTop: '1px solid color-mix(in srgb, var(--apv-sidebar-border) 40%, transparent)' }}>
            <div className={cn("flex items-center gap-3", isCollapsed && "justify-center")}>
                {/* Avatar with status dot */}
                <div className="relative shrink-0">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm border" style={{ backgroundColor: 'var(--apv-sidebar-hover)', color: 'var(--apv-accent)', borderColor: 'var(--apv-sidebar-border)' }}>
                        <Activity className="h-4 w-4 text-emerald-400" />
                    </div>
                    {/* Status dot on avatar */}
                    <span
                        className={cn(
                            'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 transition-colors',
                            cfg.dot, cfg.glow
                        )}
                        style={{ borderColor: 'var(--apv-sidebar-bg)' }}
                        title={`${label}${latency ? ` (${latency}ms)` : ''}`}
                    />
                </div>
                {!isCollapsed && (
                    <div className="overflow-hidden flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-white truncate">
                            APV-ERP v{changelogData[0]?.version || "1.0.0"}
                        </p>
                        <div className="flex items-center gap-1.5">
                            <span className={cn('inline-block h-1.5 w-1.5 rounded-full', cfg.dot)} />
                            <span className={cn('text-[10px] font-medium truncate', cfg.text)}>
                                {label}
                            </span>
                            {latency !== undefined && status === 'online' && (
                                <span className="text-[9px] text-[#8b939b] font-mono">
                                    {latency}ms
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
